import { useCallback } from "react"

import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import type { GeneralSettings, UserSettings } from "@/types/user-settings"

import { updateSettingsSection } from "@/api/user-settings-api"
import { queryKeys } from "@/lib/query-keys"

type SettingsPatch = Partial<GeneralSettings>

type ValueSnapshot = {
  exists: boolean
  value: unknown
}

type SettingsIntent = {
  id: number
  keys: string[]
  patch: SettingsPatch
}

type OwnedValue = {
  id: number
  snapshot: ValueSnapshot
}

type CoordinatorState = {
  confirmedByKey: Map<string, ValueSnapshot>
  lastManagedData?: UserSettings
  lastManagedUpdatedAt?: number
  latestByKey: Map<string, OwnedValue>
  nextId: number
}

const coordinatorStateByClient = new WeakMap<QueryClient, CoordinatorState>()

function getCoordinatorState(queryClient: QueryClient): CoordinatorState {
  const existing = coordinatorStateByClient.get(queryClient)
  if (existing) return existing

  const state: CoordinatorState = {
    confirmedByKey: new Map(),
    latestByKey: new Map(),
    nextId: 0,
  }
  coordinatorStateByClient.set(queryClient, state)
  return state
}

function compactPatch(patch: SettingsPatch): SettingsPatch {
  const compacted: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(patch as Record<string, unknown>)) {
    if (value === undefined) continue
    if (key !== "chatHomeSignals") {
      compacted[key] = value
      continue
    }
    if (typeof value !== "object" || value === null) continue

    const compactedSignals = Object.fromEntries(
      Object.entries(value).filter(([, signalValue]) => signalValue !== undefined)
    )
    if (Object.keys(compactedSignals).length > 0) {
      compacted.chatHomeSignals = compactedSignals
    }
  }

  return compacted as SettingsPatch
}

function getLogicalKeys(patch: SettingsPatch): string[] {
  return Object.entries(patch).flatMap(([key, value]) =>
    key === "chatHomeSignals"
      ? Object.keys(value).map((signalKey) => `chatHomeSignals.${signalKey}`)
      : [key]
  )
}

function readSnapshot(settings: GeneralSettings | undefined, key: string): ValueSnapshot {
  if (key.startsWith("chatHomeSignals.")) {
    const signalKey = key.slice("chatHomeSignals.".length)
    const signals = settings?.chatHomeSignals as Record<string, unknown> | undefined
    return {
      exists: signals !== undefined && Object.prototype.hasOwnProperty.call(signals, signalKey),
      value: signals?.[signalKey],
    }
  }

  const values = settings as unknown as Record<string, unknown> | undefined
  return {
    exists: values !== undefined && Object.prototype.hasOwnProperty.call(values, key),
    value: values?.[key],
  }
}

function applySnapshot(
  settings: GeneralSettings,
  key: string,
  snapshot: ValueSnapshot
): GeneralSettings {
  if (key.startsWith("chatHomeSignals.")) {
    const signalKey = key.slice("chatHomeSignals.".length)
    const hadSignals = settings.chatHomeSignals !== undefined
    const signals = { ...(settings.chatHomeSignals ?? {}) } as Record<string, unknown>
    if (snapshot.exists) {
      signals[signalKey] = snapshot.value
    } else {
      delete signals[signalKey]
    }

    const updated = { ...settings }
    if (Object.keys(signals).length > 0 || hadSignals) {
      updated.chatHomeSignals = signals
    } else {
      delete updated.chatHomeSignals
    }
    return updated
  }

  const updated = { ...settings } as unknown as Record<string, unknown>
  if (snapshot.exists) {
    updated[key] = snapshot.value
  } else {
    delete updated[key]
  }
  return updated as unknown as GeneralSettings
}

function applyLatestValues(
  settings: GeneralSettings,
  latestByKey: Map<string, OwnedValue>
): GeneralSettings {
  let next = settings
  for (const [key, owned] of latestByKey) {
    next = applySnapshot(next, key, owned.snapshot)
  }
  return next
}

function snapshotsEqual(left: ValueSnapshot, right: ValueSnapshot): boolean {
  return left.exists === right.exists && Object.is(left.value, right.value)
}

function isManagedCache(queryClient: QueryClient, state: CoordinatorState): boolean {
  return (
    queryClient.getQueryData<UserSettings>(queryKeys.userSettings) === state.lastManagedData &&
    queryClient.getQueryState(queryKeys.userSettings)?.dataUpdatedAt === state.lastManagedUpdatedAt
  )
}

function captureUnmanagedValues(
  queryClient: QueryClient,
  state: CoordinatorState,
  keys: string[]
): void {
  if (isManagedCache(queryClient, state)) return
  const settings = queryClient.getQueryData<UserSettings>(queryKeys.userSettings)?.data.settings
  for (const key of keys) {
    state.confirmedByKey.set(key, readSnapshot(settings, key))
  }
}

function markManagedCache(queryClient: QueryClient, state: CoordinatorState): void {
  state.lastManagedData = queryClient.getQueryData<UserSettings>(queryKeys.userSettings)
  state.lastManagedUpdatedAt = queryClient.getQueryState(queryKeys.userSettings)?.dataUpdatedAt
}

/**
 * Serializes general-settings PATCHes across mounts while fencing cache writes
 * independently for every submitted top-level setting or chat-home signal.
 */
export function useSerializedGeneralSettingsUpdate() {
  const queryClient = useQueryClient()
  const state = getCoordinatorState(queryClient)
  const { mutateAsync } = useMutation<UserSettings, Error, SettingsIntent>({
    mutationFn: ({ patch }) => updateSettingsSection("settings", patch as Record<string, unknown>),
    scope: { id: "general-settings" },
    onMutate: async (intent) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.userSettings })
      captureUnmanagedValues(queryClient, state, intent.keys)
      queryClient.setQueryData<UserSettings>(queryKeys.userSettings, (current) => {
        if (!current) return current

        let settings = current.data.settings
        for (const key of intent.keys) {
          const owned = state.latestByKey.get(key)
          if (owned?.id === intent.id) {
            settings = applySnapshot(settings, key, owned.snapshot)
          }
        }
        return {
          ...current,
          data: {
            ...current.data,
            settings,
          },
        }
      })
      markManagedCache(queryClient, state)
    },
    onSuccess: (updatedSettings, intent) => {
      for (const key of intent.keys) {
        state.confirmedByKey.set(key, readSnapshot(updatedSettings.data.settings, key))
      }

      queryClient.setQueryData<UserSettings>(queryKeys.userSettings, (current) => {
        const base = current
          ? {
              ...updatedSettings,
              data: {
                ...updatedSettings.data,
                ...current.data,
                settings: current.data.settings,
              },
            }
          : updatedSettings

        let settings = base.data.settings
        for (const key of intent.keys) {
          if (state.latestByKey.get(key)?.id === intent.id) {
            settings = applySnapshot(
              settings,
              key,
              readSnapshot(updatedSettings.data.settings, key)
            )
          }
        }
        if (!current) {
          settings = applyLatestValues(settings, state.latestByKey)
        }
        return {
          ...base,
          data: {
            ...base.data,
            settings,
          },
        }
      })
      markManagedCache(queryClient, state)
    },
    onError: (_error, intent) => {
      captureUnmanagedValues(queryClient, state, intent.keys)
      queryClient.setQueryData<UserSettings>(queryKeys.userSettings, (current) => {
        if (!current) return current

        let settings = current.data.settings
        for (const key of intent.keys) {
          if (state.latestByKey.get(key)?.id !== intent.id) continue
          const confirmed = state.confirmedByKey.get(key)
          if (confirmed) {
            settings = applySnapshot(settings, key, confirmed)
          }
        }
        return {
          ...current,
          data: {
            ...current.data,
            settings,
          },
        }
      })
      markManagedCache(queryClient, state)
      toast.error("Failed to update settings")
    },
    onSettled: (_data, _error, intent) => {
      for (const key of intent.keys) {
        if (state.latestByKey.get(key)?.id === intent.id) {
          state.latestByKey.delete(key)
        }
      }
    },
  })

  return useCallback(
    (requestedPatch: SettingsPatch): Promise<void> => {
      const patch = compactPatch(requestedPatch)
      const keys = getLogicalKeys(patch)
      if (keys.length === 0) return Promise.resolve()

      const cachedSettings = queryClient.getQueryData<UserSettings>(queryKeys.userSettings)?.data
        .settings
      const id = ++state.nextId
      for (const key of keys) {
        const cachedSnapshot = readSnapshot(cachedSettings, key)
        const currentOwner = state.latestByKey.get(key)
        if (
          !currentOwner ||
          !isManagedCache(queryClient, state) ||
          !snapshotsEqual(cachedSnapshot, currentOwner.snapshot)
        ) {
          state.confirmedByKey.set(key, cachedSnapshot)
        }
        state.latestByKey.set(key, {
          id,
          snapshot: readSnapshot(patch as GeneralSettings, key),
        })
      }

      return mutateAsync({ id, keys, patch }).then(
        () => undefined,
        () => undefined
      )
    },
    [mutateAsync, queryClient, state]
  )
}
